"use client";

import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import Link from "next/link";
import styles from "./CDedicatedJobOffers.module.css";

/* Backend zwraca `pills` / `technologies` jako array po `splitLines`, ale gdy
   Next.js dev-server cachuje starsza wersje modulu albo transport zwroci raw
   string, normalizujemy do array na kliencie - kazda linia = pigulka. */
function normalizeLines(raw: unknown): string[] {
  if (Array.isArray(raw))
    return raw.filter((s): s is string => typeof s === "string" && s.trim() !== "").map((s) => s.trim());
  if (typeof raw === "string")
    return raw.split(/\r\n|\r|\n/).map((s) => s.trim()).filter((s) => s !== "");
  return [];
}

/**
 * CDedicatedJobOffers - visitor view komponentu `c_dedicated_job_offers`.
 *
 * Karta oferty: tytul + pigulki meta pod tytulem + opis + pigulki technologii
 * pod opisem + przycisk aplikacji po prawej (URL/mailto z `apply_href`).
 */
export type CDedicatedJobOffersItem = {
  id: number;
  name: string;
  pills: string[];
  description: string | null;
  technologies: string[];
  apply_label: string | null;
  apply_href: string | null;
};

export type CDedicatedJobOffersContent = {
  id: number;
  symbol: string;
  title: string;
  items: CDedicatedJobOffersItem[];
};

export type CDedicatedJobOffersProps = {
  content: CDedicatedJobOffersContent;
};

export function CDedicatedJobOffers({ content }: CDedicatedJobOffersProps) {
  if (!content || typeof content !== "object" || !Array.isArray(content.items) || content.items.length === 0) {
    return null;
  }

  return (
    <section className={styles.root}>
      <ul className={styles.list}>
        {content.items.map((item) => (
          <JobCard key={item.id} item={item} />
        ))}
      </ul>
    </section>
  );
}

function JobCard({ item }: { item: CDedicatedJobOffersItem }) {
  const internal = item.apply_href ? extractInternalHref(item.apply_href) : null;
  const label = item.apply_label && item.apply_label.trim() !== "" ? item.apply_label : "Aplikuj";
  const showButton = !!item.apply_href;
  const pills = normalizeLines(item.pills);
  const techs = normalizeLines(item.technologies);

  return (
    <li className={styles.card}>
      <div className={styles.body}>
        <h3 className={styles.cardTitle}>{item.name}</h3>

        {pills.length > 0 && (
          <ul className={styles.pillList}>
            {pills.map((p, i) => (
              <li key={i} className={styles.pill}>
                {p}
              </li>
            ))}
          </ul>
        )}

        {item.description && (
          <p className={styles.cardDesc}>{item.description}</p>
        )}

        {techs.length > 0 && (
          <ul className={styles.techList}>
            {techs.map((t, i) => (
              <li key={i} className={styles.tech}>
                {t}
              </li>
            ))}
          </ul>
        )}
      </div>

      {showButton && (
        <div className={styles.applyWrap}>
          {internal !== null ? (
            <Link href={internal} className={styles.applyBtn}>
              {label} <span aria-hidden="true">&#8594;</span>
            </Link>
          ) : (
            <a
              href={item.apply_href!}
              className={styles.applyBtn}
              target={item.apply_href!.startsWith("mailto:") ? undefined : "_blank"}
              rel={item.apply_href!.startsWith("mailto:") ? undefined : "noopener noreferrer"}
            >
              {label} <span aria-hidden="true">&#8594;</span>
            </a>
          )}
        </div>
      )}
    </li>
  );
}
